import { ref } from 'vue';
import { defineStore } from 'pinia';

type SaveStatus = 'idle' | 'saving' | 'error';

// The guardrails master switch is a single app-wide setting (not per-matter),
// backed by /api/Settings/Guardrails. Mirrors the dataLoadStore modal pattern.
export const useGuardrailsStore = defineStore('guardrails', () => {
    const isOpen = ref(false);
    // null = not yet fetched; true/false = definitive result.
    const enabled = ref<boolean | null>(null);
    const saveStatus = ref<SaveStatus>('idle');
    const errorMessage = ref<string | null>(null);

    function open() {
        isOpen.value = true;
        void fetchStatus();
    }

    function close() {
        isOpen.value = false;
        saveStatus.value = 'idle';
        errorMessage.value = null;
    }

    async function fetchStatus(): Promise<void> {
        try {
            const response = await fetch('/api/Settings/Guardrails');
            if (response.ok) {
                const data = await response.json();
                enabled.value = data.enabled === true;
            }
        } catch (err) {
            console.warn('Failed to fetch guardrails setting:', err);
        }
    }

    async function setEnabled(value: boolean): Promise<void> {
        if (saveStatus.value === 'saving') return;
        saveStatus.value = 'saving';
        errorMessage.value = null;

        try {
            const response = await fetch('/api/Settings/Guardrails', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled: value }),
            });

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body?.detail ?? `Update failed (${response.status})`);
            }

            const data = await response.json();
            enabled.value = data.enabled === true;
            saveStatus.value = 'idle';
        } catch (e: unknown) {
            errorMessage.value = e instanceof Error ? e.message : 'Unknown error';
            saveStatus.value = 'error';
        }
    }

    return {
        isOpen,
        enabled,
        saveStatus,
        errorMessage,
        open,
        close,
        fetchStatus,
        setEnabled,
    };
});
